/**
 * Emplacements de camping (onglet de Refuges et campings), rangés sous leur camping parent ; seuls les
 * campings qui ont des emplacements sont listés (une organisation peut compter des dizaines de campings).
 * On change le camping d'un emplacement dans sa ligne ; les emplacements sans camping sont en tête,
 * avec un bouton pour les lier tous d'un coup. Ajouter : nom, tag tarif, camping, puis un clic sur la carte
 * (où les emplacements existants sont visibles).
 */
import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { MapPicker, type MapPickerPoint } from '@web/features/organizations/components/MapPicker'
import { existingPointsOf } from '@web/features/organizations/components/mapPickerPoints'
import { useOrgSiteMapView } from '@web/features/organizations/hooks/useOrgSiteMapView'
import {
  createOrganizationPitch,
  deleteOrganizationPitch,
  fetchOrganizationPitches,
  type OrganizationPitch,
} from '@web/features/organizations/services/organizationApi'
import { updatePitch } from '../api'

const NONE = 0

export function PitchesPanel({ orgId }: { orgId: number }) {
  const { t } = useTranslation('gestion')
  const queryClient = useQueryClient()
  const { siteView, mapKey, campings } = useOrgSiteMapView(orgId)
  const { data, isLoading } = useQuery({ queryKey: ['orgPitches', orgId], queryFn: ({ signal }) => fetchOrganizationPitches(orgId, { signal }) })
  const pitches = useMemo(() => data?.results ?? [], [data])
  const rateTags = data?.rate_tags ?? []
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [bulkTarget, setBulkTarget] = useState<number>(campings[0]?.id ?? NONE)
  const [name, setName] = useState('')
  const [rateTag, setRateTag] = useState('')
  const [campingId, setCampingId] = useState<number>(NONE)
  const [point, setPoint] = useState<MapPickerPoint | null>(null)

  const groups = useMemo(() => {
    const byCamping = new Map<number, OrganizationPitch[]>()
    for (const p of pitches) {
      const key = p.camping_id ?? NONE
      if (!byCamping.has(key)) byCamping.set(key, [])
      byCamping.get(key)!.push(p)
    }
    const label = (p: OrganizationPitch) => p.name || ''
    const sorted = (list: OrganizationPitch[] = []) => [...list].sort((a, b) => label(a).localeCompare(label(b), 'fr', { numeric: true }))
    return [
      { id: NONE, name: t('emplacements.sans_camping'), items: sorted(byCamping.get(NONE)) },
      ...campings.map((c) => ({ id: c.id, name: c.name, items: sorted(byCamping.get(c.id)) })),
    ]
  }, [pitches, campings, t])

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['orgPitches', orgId] })
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
      await refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const unlinked = groups[0].items
  const target = bulkTarget || campings[0]?.id || NONE

  if (isLoading) return <p className="g-muted">{t('commun.chargement')}</p>

  return (
    <section className="g-card g-pitches">
      <p className="g-muted">{t('emplacements.intro')}</p>
      {error && <p className="g-alert g-alert--error">{error}</p>}

      {unlinked.length > 0 && campings.length > 0 && (
        <div className="g-pitches__bulk">
          <span>{t('emplacements.n_sans_camping', { count: unlinked.length })}</span>
          <select className="g-input" value={target} onChange={(e) => setBulkTarget(Number(e.target.value))}>
            {campings.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy || !target}
            onClick={() => void run(() => Promise.all(unlinked.map((p) => updatePitch(orgId, p.id, { camping_id: target }))))}
          >
            {t('emplacements.lier_tous')}
          </button>
        </div>
      )}

      {pitches.length === 0 && <p className="g-hint">{t('emplacements.aucun')}</p>}
      {groups
        .filter((g) => g.items.length > 0)
        .map((g) => (
          <div key={g.id} className="g-equip__group">
            <h2 className="g-editor__section">
              <span className={`mdi ${g.id === NONE ? 'mdi-help-circle-outline' : 'mdi-tent'}`} aria-hidden /> {g.name}{' '}
              <span className="g-muted">({g.items.length})</span>
            </h2>
            <ul className="g-editor__list">
                {g.items.map((p) => (
                  <li key={p.id} className="g-pitches__row">
                    <span className="g-pitches__name">
                      <strong>{p.name || t('emplacements.sans_nom', { id: p.id })}</strong>
                      {(p.rate_tag_label || p.rate_tag) && <span className="g-muted"> · {p.rate_tag_label || p.rate_tag}</span>}
                    </span>
                    <label className="g-pitches__parent">
                      <span className="g-sr-only">{t('emplacements.camping_parent')}</span>
                      <select
                        className="g-input"
                        value={p.camping_id ?? NONE}
                        disabled={busy}
                        onChange={(e) => {
                          const next = Number(e.target.value)
                          void run(() => updatePitch(orgId, p.id, { camping_id: next || null }))
                        }}
                      >
                        <option value={NONE}>{t('emplacements.aucun_camping')}</option>
                        {campings.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    {p.location.lat != null && (
                      <Link className="g-link" to={`/o/${orgId}/carte?emplacement=${p.id}`}>
                        {t('equipements.voir_carte')}
                      </Link>
                    )}
                    <button
                      type="button"
                      className="g-link g-danger"
                      disabled={busy}
                      onClick={() => window.confirm(t('emplacements.supprimer_confirm', { name: p.name || p.id })) && void run(() => deleteOrganizationPitch(orgId, p.id))}
                    >
                      {t('commun.supprimer')}
                    </button>
                  </li>
                ))}
            </ul>
          </div>
        ))}

      <div className="g-pitches__add">
        <h2 className="g-editor__section">{t('emplacements.ajouter')}</h2>
        <div className="g-pitches__fields">
          <label className="g-field">
            <span className="g-field__label">{t('carte.nom')}</span>
            <input className="g-input" value={name} placeholder={t('emplacements.ex_nom')} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="g-field">
            <span className="g-field__label">{t('emplacements.tag_tarif')}</span>
            <select className="g-input" value={rateTag || rateTags[0]?.slug || ''} onChange={(e) => setRateTag(e.target.value)}>
              {rateTags.map((r) => (
                <option key={r.slug} value={r.slug}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          <label className="g-field">
            <span className="g-field__label">{t('emplacements.camping_parent')}</span>
            <select className="g-input" value={campingId || campings[0]?.id || NONE} onChange={(e) => setCampingId(Number(e.target.value))}>
              <option value={NONE}>{t('emplacements.aucun_camping')}</option>
              {campings.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="g-hint">{point ? t('emplacements.point_choisi') : t('emplacements.cliquer_carte')}</p>
        <MapPicker
          key={`g-pitch-${mapKey}`}
          value={point}
          onChange={setPoint}
          defaultCenter={siteView.center}
          defaultZoom={siteView.zoom}
          fitBounds={siteView.fitBounds}
          height="min(420px, 50vh)"
          showCoordinateFields={false}
          existingPoints={existingPointsOf(pitches, (p) => p.name)}
        />
        <div className="g-editor__buttons">
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy || !point}
            onClick={() =>
              point &&
              void run(async () => {
                const parent = campingId || campings[0]?.id || NONE
                await createOrganizationPitch(orgId, {
                  name: name.trim() || undefined,
                  rate_tag: rateTag || rateTags[0]?.slug,
                  camping_id: parent || null,
                  location: point,
                })
                setName('')
                setPoint(null)
              })
            }
          >
            {busy ? t('commun.enregistrement') : t('emplacements.ajouter_btn')}
          </button>
        </div>
      </div>
    </section>
  )
}
